// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

/**
 * Fetch user notification preferences
 */
export const fetchNotificationPreferences = createAsyncThunk(
  'notificationPreferences/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/v1/users/me/preferences')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notification preferences')
    }
  }
)

/**
 * Update user notification preferences
 */
export const updateNotificationPreferences = createAsyncThunk(
  'notificationPreferences/update',
  async (preferences, { rejectWithValue }) => {
    try {
      const response = await axios.put('/v1/users/me/preferences', preferences)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update notification preferences')
    }
  }
)

export const notificationPreferencesSlice = createSlice({
  name: 'notificationPreferences',
  initialState: {
    paidNotificationsEnabled: true,
    overdueNotificationsEnabled: true,
    rateReviewNotificationsEnabled: true,
    engagementNotificationsEnabled: true,
    teamsAppInstalled: null, // null = not loaded yet, true/false = loaded
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: builder => {
    builder
      // fetchNotificationPreferences
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false
        state.paidNotificationsEnabled = action.payload.paidNotificationsEnabled ?? true
        state.overdueNotificationsEnabled = action.payload.overdueNotificationsEnabled ?? true
        state.rateReviewNotificationsEnabled = action.payload.rateReviewNotificationsEnabled ?? true
        state.engagementNotificationsEnabled = action.payload.engagementNotificationsEnabled ?? true
        state.teamsAppInstalled = action.payload.teamsAppInstalled ?? false
        state.error = null
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // updateNotificationPreferences
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false
        state.paidNotificationsEnabled = action.payload.paidNotificationsEnabled ?? true
        state.overdueNotificationsEnabled = action.payload.overdueNotificationsEnabled ?? true
        state.rateReviewNotificationsEnabled = action.payload.rateReviewNotificationsEnabled ?? true
        state.engagementNotificationsEnabled = action.payload.engagementNotificationsEnabled ?? true
        state.teamsAppInstalled = action.payload.teamsAppInstalled ?? state.teamsAppInstalled
        state.error = null
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError } = notificationPreferencesSlice.actions

export default notificationPreferencesSlice.reducer
